/**
 * TypeaheadPrimitives — SPD v5.83 Phase 2 (2026-07-14)
 * ────────────────────────────────────────────────────
 * Six layout primitives + shared building blocks for the predictive-
 * typeahead dropdown. Each primitive reads the classifier's 12-field
 * JSON contract and renders in the "v3 ambient" style approved by the
 * operator on the /preview/primitives-mockup-v3 route:
 *
 *   · No container box · no card chrome · no headers
 *   · Chips are plain cyan text · hairline underline on hover
 *   · LLM-authored `intro_line` sits in italic cyan directly below
 *     the search input as a conversational acknowledgement of the
 *     query's shape
 *   · `Other [eg: X]` chip that turns into an inline input on click
 *   · Micro-animation only (stagger fade-in · hover glow)
 *
 * Every static string that ships is baselined in the copy-guard
 * manifest — nothing user-facing here is invented at runtime.
 *
 * Canadian English throughout.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  shouldShowOptionsView,
  pickExampleHints,
} from '../../hooks/optionsViewPredicate';

// ── Shared building blocks (reused across primitives) ─────────

// Plain cyan text chip. Hairline underline slides in on hover.
export const PlainChip = ({ label, onClick, delay = 0, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    style={{ animationDelay: `${delay}ms` }}
    className="ta-fade group text-left inline-block text-cyan-400 text-[15px] font-normal hover:text-cyan-200 transition-colors relative"
  >
    <span className="relative">{label}</span>
    <span className="absolute -bottom-0.5 left-0 right-0 h-px bg-transparent group-hover:bg-cyan-300 transition-all duration-200" />
  </button>
);

// `Other [for example: A, B, or C]` chip · FSD v1.11 REDLINE-02 · IJ-230.
// Renders as plain cyan text. On click, the hint portion becomes an
// inline input using the first example as placeholder. Never renders
// a container box.
//
// examples          — string[] · up to three example hints. Empty array
//                     → low-signal placeholder '[type here to keep
//                     searching]' per FSD FR-10.
// onSubmit(value, exitKind)
//                   — fired on Enter · exitKind='other-field' per
//                     REDLINE-34 E2 so parent analytics can tag the
//                     handoff correctly.
export const OtherChip = ({ examples = [], onSubmit, delay = 0 }) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const list = Array.isArray(examples) ? examples.filter(Boolean).map(String) : [];
  const lowSignal = list.length === 0;
  const primaryPlaceholder = lowSignal
    ? 'type here to keep searching'
    : list[0];

  // Build the italic hint phrase per FSD REDLINE-02:
  //   3+ examples → '[for example: A, B, or C]'
  //   2 examples  → '[for example: A, or B]'
  //   1 example   → '[for example: A]'
  //   0 examples  → '[type here to keep searching]'
  const hintPhrase = lowSignal
    ? '[type here to keep searching]'
    : list.length === 1
      ? `[for example: ${list[0]}]`
      : list.length === 2
        ? `[for example: ${list[0]}, or ${list[1]}]`
        : `[for example: ${list[0]}, ${list[1]}, or ${list[2]}]`;

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={lowSignal ? 'ta-other-chip-empty-signal' : 'ta-other-chip'}
        onClick={() => setEditing(true)}
        style={{ animationDelay: `${delay}ms` }}
        className="ta-fade text-left inline-flex items-baseline gap-1 text-[15px] font-normal group"
      >
        <span className="text-cyan-400 group-hover:text-cyan-200">Other</span>
        <span className="text-cyan-400/60 group-hover:text-cyan-300/80 italic"> {hintPhrase}</span>
      </button>
    );
  }
  return (
    <span className="inline-flex items-baseline gap-1 text-[15px] font-normal" data-testid="ta-other-input-row">
      <span className="text-cyan-400">Other</span>
      <span className="text-cyan-400/60"> [</span>
      <input
        ref={inputRef}
        type="text"
        placeholder={primaryPlaceholder}
        data-testid="ta-other-input"
        className="bg-transparent text-cyan-200 placeholder:text-cyan-500/50 focus:outline-none border-b border-cyan-500/30 focus:border-cyan-300 min-w-[16rem]"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.value.trim() && onSubmit) {
            // REDLINE-34 E2 · Other-field submit tags the exit kind so
            // parent analytics can log options_exit_kind='other-field'.
            onSubmit(e.target.value.trim(), 'other-field');
          } else if (e.key === 'Escape') {
            setEditing(false);
          }
        }}
        onBlur={(e) => { if (!e.target.value) setEditing(false); }}
      />
      <span className="text-cyan-400/60">]</span>
    </span>
  );
};

// The LLM-authored conversational intro. Italic cyan. Slow fade-in.
// SPD v5.86 hotfix (2026-02-14) — the CSS class already applies italic
// styling; some LLM outputs still wrap the whole intro in markdown
// italic markers (`_..._` or `*...*`) which then get rendered LITERALLY
// on top of the CSS italic. Strip them before render so the operator's
// "_Running shoes — a big category. Are you looking...pair?_" turns
// into a clean italic line without visible underscores at the ends.
export const IntroLine = ({ text }) => {
  if (!text) return null;
  const cleaned = String(text)
    .trim()
    // Peel a matching pair of _..._ or *...* off the outer edges.
    // Repeat up to 3 times to catch `__hello__` style double-wraps.
    .replace(/^([_*])+\s*(.*?)\s*\1+$/s, '$2')
    .replace(/^([_*])+\s*(.*?)\s*\1+$/s, '$2')
    .replace(/^([_*])+\s*(.*?)\s*\1+$/s, '$2')
    .trim();
  return (
    <p
      data-testid="ta-intro-line"
      className="ta-fade-slow text-cyan-400 text-[15px] italic leading-relaxed"
    >
      {cleaned}
    </p>
  );
};

// 2-column plain-text chip grid. Left-aligned to input's edge.
// Grid uses gap-x-10 (was gap-x-16 pre-2026-07-14) so chip labels sit
// closer horizontally per operator's edited mock — feels more like a
// tight list than a widely-spaced two-column layout.
export const ChipsTwoCol = ({ chips, onPick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 max-w-2xl">
    {chips.map((c, i) => (
      <PlainChip
        key={`${c}-${i}`}
        label={c}
        onClick={() => onPick && onPick(c)}
        delay={i * 30}
        testid={`ta-chip-${i}`}
      />
    ))}
  </div>
);

// Inline-labelled row: `Any budget?  Under $100 · $100–$500 · $500+`.
// Used inside Compare and Products primitives to layer secondary
// narrowing (city / retailer / budget) below the primary chips.
export const InlineTextRow = ({ label, icon, chips, onPick, input, placeholder, testidPrefix }) => (
  <div className="flex items-baseline gap-4 text-[15px] flex-wrap">
    <span className="text-slate-500 italic min-w-[120px] flex items-center gap-1">
      {icon}
      {label}
    </span>
    {input ? (
      <input
        type="text"
        placeholder={placeholder}
        data-testid={`ta-inline-input-${testidPrefix}`}
        className="bg-transparent text-cyan-200 placeholder:text-slate-600 focus:outline-none border-b border-transparent focus:border-cyan-300 pb-0.5 min-w-[16rem]"
      />
    ) : (
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        {chips.map((c, i) => (
          <PlainChip
            key={c}
            label={c}
            onClick={() => onPick && onPick(c)}
            delay={i * 40}
            testid={`ta-inline-${testidPrefix}-${i}`}
          />
        ))}
      </div>
    )}
  </div>
);

// ── Six primitives ───────────────────────────────────────────
// Each takes the full 12-field typeahead response + a small handful
// of callbacks. All rendering decisions are driven by the data — no
// primitive invents chip labels or intro text.

// TOPIC — the default for informational / opinion / navigational.
// Just the intro + a 2-col chip grid + Other. Nothing else.
export const TopicPrimitive = ({ data, onPickChip, onPickFreetext }) => (
  <div className="mt-4 space-y-5" data-testid="ta-primitive-topic">
    <IntroLine text={data.intro_line} />
    {data.chips && data.chips.length > 0 && (
      <ChipsTwoCol chips={data.chips} onPick={onPickChip} />
    )}
    <OtherChip examples={data.freetext_hint ? [data.freetext_hint] : ['Anything more specific?']} onSubmit={onPickFreetext} />
  </div>
);

// COMPARE — commerce_category. Intro + tightly-spaced 2-col chip grid +
// Other freetext row. NOTHING ELSE. Per operator directive 2026-07-14:
// - No inline "Where should I look?" (duplicates the Source buttons below
//   the primitive that already exist in the KG page chrome)
// - No "Any city?" input (weird — city is a query modifier, not a chip)
// - No "Any budget?" preset row (over-featured)
// Grid uses gap-x-10 instead of gap-x-16 so chip labels sit closer.
export const ComparePrimitive = ({ data, onPickChip, onPickFreetext }) => (
  <div className="mt-4 space-y-4" data-testid="ta-primitive-compare">
    <IntroLine text={data.intro_line} />
    {data.chips && data.chips.length > 0 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 max-w-2xl">
        {data.chips.map((c, i) => (
          <PlainChip
            key={`${c}-${i}`}
            label={c}
            onClick={() => onPickChip && onPickChip(c)}
            delay={i * 30}
            testid={`ta-chip-${i}`}
          />
        ))}
      </div>
    )}
    <OtherChip examples={data.freetext_hint ? [data.freetext_hint] : ['for men', 'under $150', 'size 10 waterproof']} onSubmit={onPickFreetext} />
  </div>
);

// PRODUCTS — commerce_specific. Intro + product completions +
// retailer chips (from `chips` if commerce_type style).
export const ProductsPrimitive = ({ data, onPickChip, onPickCompletion, onPickFreetext }) => (
  <div className="mt-4 space-y-5" data-testid="ta-primitive-products">
    <IntroLine text={data.intro_line} />
    {data.completions && data.completions.length > 0 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-16 gap-y-3 max-w-2xl">
        {data.completions.map((c, i) => (
          <PlainChip
            key={`${c}-${i}`}
            label={c}
            onClick={() => onPickCompletion && onPickCompletion(c)}
            delay={i * 40}
            testid={`ta-completion-${i}`}
          />
        ))}
      </div>
    )}
    {data.chips && data.chips.length > 0 && (
      <InlineTextRow
        label="Retailer?"
        chips={data.chips}
        onPick={onPickChip}
        testidPrefix="retailer"
      />
    )}
    <OtherChip examples={data.freetext_hint ? [data.freetext_hint] : ['Anything more specific?']} onSubmit={onPickFreetext} />
  </div>
);

// LENS — informational_mixed with persona chips. Same 2-col grid.
export const LensPrimitive = ({ data, onPickChip, onPickFreetext }) => (
  <div className="mt-4 space-y-5" data-testid="ta-primitive-lens">
    <IntroLine text={data.intro_line} />
    {data.chips && data.chips.length > 0 && (
      <ChipsTwoCol chips={data.chips} onPick={onPickChip} />
    )}
    <OtherChip examples={data.freetext_hint ? [data.freetext_hint] : ['Anything more specific?']} onSubmit={onPickFreetext} />
  </div>
);

// STEPS — informational_mixed with a step-by-step process. Plain
// text numbered list. Chips render as the step titles.
export const StepsPrimitive = ({ data, onPickChip, onPickFreetext }) => (
  <div className="mt-4 space-y-5" data-testid="ta-primitive-steps">
    <IntroLine text={data.intro_line} />
    {data.chips && data.chips.length > 0 && (
      <ol className="space-y-2">
        {data.chips.map((step, i) => (
          <li
            key={`${step}-${i}`}
            style={{ animationDelay: `${i * 60}ms` }}
            className="ta-fade group flex items-baseline gap-3"
          >
            <span className="text-cyan-500/50 font-mono text-xs w-6">0{i + 1}</span>
            <button
              type="button"
              onClick={() => onPickChip && onPickChip(step)}
              data-testid={`ta-step-${i}`}
              className="text-cyan-400 text-[15px] hover:text-cyan-200 transition-colors relative"
            >
              {step}
              <span className="absolute -bottom-0.5 left-0 right-0 h-px bg-transparent group-hover:bg-cyan-300 transition-all" />
            </button>
          </li>
        ))}
      </ol>
    )}
    <OtherChip examples={data.freetext_hint ? [data.freetext_hint] : ['Anything more specific?']} onSubmit={onPickFreetext} />
  </div>
);

// CALC — informational_mixed with a numeric estimator. Chips act
// as parameter narrowers (Fixed rate · Variable rate · …). No live
// calculator wired here — that's a follow-up SPD. This primitive
// just presents the parameter picks + freetext for now.
export const CalculatorPrimitive = ({ data, onPickChip, onPickFreetext }) => (
  <div className="mt-4 space-y-5" data-testid="ta-primitive-calc">
    <IntroLine text={data.intro_line} />
    {data.chips && data.chips.length > 0 && (
      <ChipsTwoCol chips={data.chips} onPick={onPickChip} />
    )}
    <OtherChip examples={data.freetext_hint ? [data.freetext_hint] : ['Anything more specific?']} onSubmit={onPickFreetext} />
  </div>
);

// ── OptionsPrimitive · IJ-230 · Wave 3c · IJ-231 · Rich Option Set ─
// FR-10 vague-query disambiguation · REDLINE-17 three-by-two grid
// layout · REDLINE-15 modal switch mutual exclusion.
//
// Renders when `shouldShowOptionsView(data)` returns true. Three
// sections top-to-bottom:
//   1. IntroLine (if present)
//   2. Three-by-two grid of chip buttons (up to six slots).
//      IJ-231 (2026-08-01) — When `data.option_set` is a non-empty
//      array, render each slot as a rich chip: the chip label on top
//      plus the `one_line_hint` in muted 13px text below. Otherwise
//      fall back to the plain `data.chips` array (graceful degrade).
//      Clicks on a rich slot pass the `sub_topic_id` as the second
//      onPickChip argument so the parent can route to the taxonomy
//      anchor instead of running a keyword search.
//   3. Refreshed OtherChip below the grid. Examples drawn from the
//      first three chips (or option_set labels) per FSD REDLINE-02.
//      Empty renders '[type here to keep searching]'.
//
// REDLINE-15 · never renders alongside the Predictions view — the
// dispatcher below routes to exactly one primitive per call.
export const OptionsPrimitive = ({ data, onPickChip, onPickFreetext }) => {
  const optionSet = Array.isArray(data.option_set) ? data.option_set.slice(0, 6) : [];
  const chips = Array.isArray(data.chips) ? data.chips.slice(0, 6) : [];
  const useRich = optionSet.length > 0;
  // Examples for the Other field · rich labels first, then chips.
  const labelSource = useRich ? optionSet.map((o) => o.label) : chips;
  const examples = pickExampleHints(labelSource);
  return (
    <div className="mt-3 flex flex-col gap-3" data-testid="ta-options-primitive">
      <IntroLine text={data.intro_line} />
      {useRich ? (
        <div className="grid grid-cols-3 gap-x-6 gap-y-3" data-testid="ta-options-grid-rich">
          {optionSet.map((slot, i) => (
            <button
              type="button"
              key={`${slot.label}-${i}`}
              data-testid={`ta-options-rich-slot-${i}`}
              onClick={() => onPickChip && onPickChip(slot.label, slot.sub_topic_id || '')}
              style={{ animationDelay: `${i * 40}ms` }}
              className="ta-fade text-left flex flex-col gap-0.5 group"
            >
              <span className="text-cyan-300 group-hover:text-cyan-100 text-[15px]">{slot.label}</span>
              {slot.one_line_hint && (
                <span className="text-cyan-400/50 group-hover:text-cyan-300/70 text-[13px] leading-snug italic">
                  {slot.one_line_hint}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : chips.length > 0 && (
        <div className="grid grid-cols-3 gap-x-6 gap-y-2.5" data-testid="ta-options-grid">
          {chips.map((label, i) => (
            <PlainChip
              key={`${label}-${i}`}
              label={label}
              onClick={() => onPickChip && onPickChip(label, '')}
              delay={i * 40}
              testid={`ta-options-slot-${i}`}
            />
          ))}
        </div>
      )}
      <OtherChip examples={examples} onSubmit={onPickFreetext} delay={(useRich ? optionSet.length : chips.length) * 40} />
    </div>
  );
};

// ── Router ────────────────────────────────────────────────────
// REDLINE-15 · Modal switch: if the payload triggers Options view,
// render OptionsPrimitive and stop. Otherwise dispatch on layout_hint.
// If the hint is missing or unknown, fall back to Topic (safest — just
// intro + chips, no assumptions about commerce shape).
export const PrimitiveByLayoutHint = (props) => {
  if (shouldShowOptionsView(props.data)) {
    return <OptionsPrimitive {...props} />;
  }
  const hint = String(props.data.layout_hint || '').toLowerCase();
  switch (hint) {
    case 'two_col_compare':    return <ComparePrimitive    {...props} />;
    case 'card_grid':          return <ProductsPrimitive   {...props} />;
    case 'prose_and_sources':  return <TopicPrimitive      {...props} />;
    case 'lens_grid':          return <LensPrimitive       {...props} />;
    case 'step_by_step':       return <StepsPrimitive      {...props} />;
    case 'calculator':         return <CalculatorPrimitive {...props} />;
    default:                    return <TopicPrimitive      {...props} />;
  }
};

// Inject the shared keyframes once per page load.
export const injectPrimitiveStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ta-primitive-styles')) return;
  const st = document.createElement('style');
  st.id = 'ta-primitive-styles';
  st.textContent = `
    @keyframes taFade     { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes taFadeSlow { from { opacity: 0; } to { opacity: 1; } }
    .ta-fade      { animation: taFade 0.35s ease-out both; }
    .ta-fade-slow { animation: taFadeSlow 0.7s ease-out both; }
  `;
  document.head.appendChild(st);
};
